import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Search, Star, FileText, Wand2, Folder, FolderPlus, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Header } from "@/components/Header";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AGE_GROUPS, LANGUAGES, label } from "@/lib/activityData";

export default function MyActivities() {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [ageGroup, setAgeGroup] = useState("all");
  const [language, setLanguage] = useState("all");
  const [folders, setFolders] = useState({ default: [], custom: [] });
  const [folder, setFolder] = useState(null);
  const [newFolder, setNewFolder] = useState("");
  const [folderOpen, setFolderOpen] = useState(false);

  const loadFolders = useCallback(() => {
    api.get("/folders").then(({ data }) => setFolders(data)).catch(() => {});
  }, []);
  useEffect(() => { loadFolders(); }, [loadFolders]);

  const load = useCallback(() => {
    const params = {};
    if (tab === "favorites") params.favorites = true;
    if (q) params.q = q;
    if (ageGroup !== "all") params.age_group = ageGroup;
    if (language !== "all") params.language = language;
    if (folder) params.folder = folder;
    api.get("/activities", { params }).then(({ data }) => setItems(data)).catch(() => {});
  }, [tab, q, ageGroup, language, folder]);

  useEffect(() => {
    const id = setTimeout(load, 250);
    return () => clearTimeout(id);
  }, [load]);

  const createFolder = async () => {
    if (!newFolder.trim()) return;
    try { await api.post("/folders", { name: newFolder.trim() }); toast.success(lang === "es" ? "Carpeta creada." : "Folder created."); setNewFolder(""); setFolderOpen(false); loadFolders(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const [dragOver, setDragOver] = useState(null);
  const assignToFolder = async (activityId, folderName) => {
    try {
      await api.put(`/activities/${activityId}`, { folder: folderName });
      toast.success(lang === "es" ? `Movido a "${folderName}"` : `Moved to "${folderName}"`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const deleteFolder = async (id, name) => {
    try {
      await api.delete(`/folders/${id}`);
      toast.success(lang === "es" ? "Carpeta eliminada." : "Folder deleted.");
      if (folder === name) setFolder(null);
      loadFolders(); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const dropProps = (folderName) => ({
    onDragOver: (e) => { e.preventDefault(); setDragOver(folderName); },
    onDragLeave: () => setDragOver((d) => (d === folderName ? null : d)),
    onDrop: (e) => { e.preventDefault(); const id = e.dataTransfer.getData("text/plain"); setDragOver(null); if (id) assignToFolder(id, folderName); },
  });

  const folderList = [
    ...folders.default.map((n) => ({ name: n })),
    ...folders.custom.map((f) => ({ name: f.name, id: f.id })),
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <h1 className="font-heading font-extrabold text-3xl tracking-tight">{t("myActivities.title")}</h1>

        <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="rounded-full bg-muted">
              <TabsTrigger value="all" className="rounded-full" data-testid="tab-all">{t("myActivities.all")}</TabsTrigger>
              <TabsTrigger value="favorites" className="rounded-full" data-testid="tab-favorites">{t("myActivities.favorites")}</TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="flex flex-wrap gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("common.search")} className="h-11 rounded-xl pl-9 w-48" data-testid="search-input" />
            </div>
            <Select value={ageGroup} onValueChange={setAgeGroup}>
              <SelectTrigger className="h-11 rounded-xl w-44" data-testid="filter-age"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("myActivities.all")} — {t("dashboard.ageGroup")}</SelectItem>
                {AGE_GROUPS.map((a) => <SelectItem key={a.value} value={a.value}>{label(a, lang)}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="h-11 rounded-xl w-40" data-testid="filter-language"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("myActivities.all")} — {t("generator.language")}</SelectItem>
                {LANGUAGES.map((l) => <SelectItem key={l.value} value={l.value}>{label(l, lang)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-muted-foreground flex items-center gap-1"><Folder className="h-4 w-4" /> {lang === "es" ? "Carpetas:" : "Folders:"}</span>
          <button onClick={() => setFolder(null)} data-testid="folder-all"
            className={`rounded-full px-3 py-1 text-sm border ${!folder ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"}`}>{lang === "es" ? "Todas" : "All"}</button>
          {folderList.map((item) => {
            const f = item.name;
            const activeF = folder === f;
            return (
              <div key={f} {...dropProps(f)} data-testid={`folder-${f.replace(/\W+/g, "-")}`}
                onClick={() => setFolder(f)}
                className={`group inline-flex items-center gap-1 rounded-full pl-3 pr-2 py-1 text-sm border cursor-pointer transition-colors ${dragOver === f ? "ring-2 ring-secondary bg-secondary/10 border-secondary" : activeF ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"}`}>
                <span>{f}</span>
                {item.id && (
                  <button onClick={(e) => { e.stopPropagation(); deleteFolder(item.id, f); }} aria-label="delete folder"
                    data-testid={`del-folder-${f.replace(/\W+/g, "-")}`}
                    className={`rounded-full p-0.5 opacity-60 hover:opacity-100 ${activeF ? "hover:bg-primary-foreground/20" : "hover:bg-muted"}`}>
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            );
          })}
          <Dialog open={folderOpen} onOpenChange={setFolderOpen}>
            <DialogTrigger asChild>
              <button className="rounded-full px-3 py-1 text-sm border border-dashed border-border text-muted-foreground flex items-center gap-1" data-testid="new-folder-btn"><FolderPlus className="h-4 w-4" /> {lang === "es" ? "Nueva" : "New"}</button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{lang === "es" ? "Nueva carpeta" : "New folder"}</DialogTitle></DialogHeader>
              <Input value={newFolder} onChange={(e) => setNewFolder(e.target.value)} placeholder={lang === "es" ? "Nombre de carpeta" : "Folder name"} className="rounded-xl" data-testid="new-folder-input" />
              <DialogFooter><Button onClick={createFolder} className="rounded-full bg-secondary hover:bg-secondary/90" data-testid="new-folder-save">{t("common.save")}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <p className="text-xs text-muted-foreground -mt-2 hidden sm:block">{lang === "es" ? "Consejo: arrastra una tarjeta de actividad a una carpeta para organizarla." : "Tip: drag an activity card onto a folder to organize it."}</p>

        {items.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            <FileText className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <p>{t("myActivities.empty")}</p>
            <Link to="/create" className="text-accent font-semibold hover:underline mt-2 inline-block">{t("dashboard.cards.createActivity")}</Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((a) => (
              <Link key={a.id} to={`/activities/${a.id}`} data-testid={`activity-card-${a.id}`}
                draggable
                onDragStart={(e) => e.dataTransfer.setData("text/plain", a.id)}
                className="rounded-2xl border border-border bg-card p-5 hover:-translate-y-1 hover:border-primary/40 transition-transform block cursor-grab active:cursor-grabbing">
                <div className="flex items-start justify-between gap-2">
                  <span className="h-10 w-10 rounded-xl bg-muted flex items-center justify-center">
                    {a.kind === "bilingual" ? <Wand2 className="h-5 w-5 text-accent" /> : <FileText className="h-5 w-5 text-primary" />}
                  </span>
                  {a.is_favorite && <Star className="h-4 w-4 fill-secondary text-secondary" />}
                </div>
                <h3 className="font-heading font-bold text-lg mt-3 line-clamp-2">{a.title}</h3>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {a.params?.age_group && <Badge variant="secondary" className="rounded-full bg-muted text-muted-foreground text-xs">{a.params.age_group}</Badge>}
                  {a.params?.theme && <Badge variant="secondary" className="rounded-full bg-muted text-muted-foreground text-xs">{a.params.theme}</Badge>}
                  {a.folder && <Badge variant="secondary" className="rounded-full bg-primary/10 text-primary text-xs">{a.folder}</Badge>}
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
